import * as React from "react";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { Grid } from "@mui/material";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { SaveDataObj } from "source/framework/saveData";
import { Base } from "ui/Scripts/source/framework/base";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { DrawerContent } from "source/components/framework/Drawer/DrawerContent";
import { PageFilterConditionDrawer } from "source/components/framework/Page/PageFilterConditionDrawer";
import { TaskPageFilterDrawerTaskTypeParameters } from "./TaskPageFilterDrawerTaskTypeParameters";
import { ITaskPageFilterLists, ITaskPageFilterState, TaskStateIdFilterActive } from "./TaskPageUtils";

export interface ITaskPageFilterDrawerProp extends ITaskPageFilterLists, ITaskPageFilterState {
    open: boolean;
    filter: IListFilterItem;
    canRemoveSharedFilters: boolean;
    stateIsReadOnly?: boolean;
    onSaveFilter: (saveAs: boolean, onGetGeneralFilter: () => any) => void;
    onRemoveFilter: (filterId: string) => void;
    onOk: (closeDialog: boolean, filterState: ITaskPageFilterState) => any;
    onCancel: () => void;
}

export interface ITaskPageFilterDrawerState extends ITaskPageFilterState {
    taskType: ITaskEditTypeItem;
    headerParameters: ITaskDataRow;
    formParameters: ITaskDataRow;
}

export class TaskPageFilterDrawer extends React.Component<ITaskPageFilterDrawerProp, ITaskPageFilterDrawerState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: ITaskPageFilterDrawerProp): ITaskPageFilterDrawerState => {
        return {
            ...props,
            ...TaskPageFilterDrawerTaskTypeParameters.getStateChangeFromTaskType(TaskPageFilterDrawerTaskTypeParameters.getNewTaskType(props.taskTypes, props.selectedTaskTypeIds), props.selectedTaskTypeHeaderFields, props.selectedTaskTypeFields)
        };
    };

    constructor(props: ITaskPageFilterDrawerProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: ITaskPageFilterDrawerProp, prevState: ITaskPageFilterDrawerState): void {
        const props = this.props;
        if (!prevProps ||
            (prevProps.filter?.getHash() === props.filter?.getHash() &&
            prevProps.selectedTaskTypeFields === props.selectedTaskTypeFields &&
            prevProps.selectedTaskTypeHeaderFields === props.selectedTaskTypeHeaderFields &&
            Base.equalStrArrays(prevProps.selectedUnitIds, props.selectedUnitIds) &&
            Base.equalStrArrays(prevProps.selectedStateIds, props.selectedStateIds) &&
            Base.equalStrArrays(prevProps.selectedPriorityIds, props.selectedPriorityIds) &&
            Base.equalStrArrays(prevProps.selectedTaskTypeIds, props.selectedTaskTypeIds) &&
            Base.equalStrArrays(prevProps.selectedHandlerUserCodes, props.selectedHandlerUserCodes) &&
            Base.equalStrArrays(prevProps.selectedPersonInChargeUserCodes, props.selectedPersonInChargeUserCodes) &&
            Base.equalStrArrays(prevProps.selectedHandlerUserCodes, props.selectedHandlerUserCodes))) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeStateIds = (selectedStateIds: string[]) => {
        this.setState({
            selectedStateIds
        });
    };

    handleChangePriorityIds = (selectedPriorityIds: string[]) => {
        this.setState({
            selectedPriorityIds
        });
    };

    handleChangeTaskTypeIds = (selectedTaskTypeIds: string[]) => {
        const props = this.props;
        this.setState({
            ...TaskPageFilterDrawerTaskTypeParameters.getChangedStateAdterChangeTaskTypeIds(props, selectedTaskTypeIds)
        });
    };

    handleChangePersonInChargeUserCodes = (selectedPersonInChargeUserCodes: string[]) => {
        this.setState({
            selectedPersonInChargeUserCodes
        });
    };

    handleChangeHandlerUserCodes = (selectedHandlerUserCodes: string[]) => {
        this.setState({
            selectedHandlerUserCodes
        });
    };

    handleSetFormParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const stateChange = TaskPageFilterDrawerTaskTypeParameters.getFormParametersState(this.state, taskDataRowCols);
        if (!stateChange) return;
        this.setState(stateChange);
    };

    handleSetHeaderParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const stateChange = TaskPageFilterDrawerTaskTypeParameters.getExtraHeaderParametersState(this.state, taskDataRowCols);
        if (!stateChange) return;
        this.setState(stateChange);
    };

    getSaveDataFromState = (_props: ITaskPageFilterDrawerProp, state: ITaskPageFilterDrawerState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("selectedTaskTypeFields", state.selectedTaskTypeFields ? state.selectedTaskTypeFields : null);
        result.append("selectedTaskTypeHeaderFields", state.selectedTaskTypeHeaderFields ? state.selectedTaskTypeHeaderFields : null);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedPriorityIds", state.selectedPriorityIds);
        result.append("selectedTaskTypeIds", state.selectedTaskTypeIds);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedPersonInChargeUserCodes", state.selectedPersonInChargeUserCodes);
        return result;
    };

    static filterIsSet = (filterLists: ITaskPageFilterLists, filterState: ITaskPageFilterState): boolean => {
        return !(
            (filterState.selectedTaskTypeFields ?? "") === "" &&
            (filterState.selectedTaskTypeHeaderFields ?? "") === "" &&
            Base.equalStrArrays(filterState.selectedUnitIds, [filterLists.units[0].id]) &&
            Base.equalStrArrays(filterState.selectedStateIds, [filterLists.states[0].id]) &&
            Base.equalStrArrays(filterState.selectedPriorityIds, [filterLists.priorities[0].id]) &&
            Base.equalStrArrays(filterState.selectedTaskTypeIds, [filterLists.taskTypes[0].id]) &&
            Base.equalStrArrays(filterState.selectedHandlerUserCodes, [filterLists.handlers[0].id]) &&
            Base.equalStrArrays(filterState.selectedPersonInChargeUserCodes, [filterLists.personsInCharge[0].id]));
    };

    handleSaveFilter = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        if (!saveAs) {
            props.onOk(false, state);
            await this.props.onSaveFilter(false, null);
        } else {
            await this.props.onSaveFilter(true, () => {
                return props.onOk(false, state);
            });
        }
    };

    static getClearedParametersState = (props: ITaskPageFilterLists) => {
        const selectedTaskTypeIds = [props.taskTypes[0].id];
        return {
            selectedUnitIds: [props.units[0].id],
            selectedStateIds: [props.states[0].id],
            selectedPriorityIds: [props.priorities[0].id],
            selectedTaskTypeIds,
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedPersonInChargeUserCodes: [props.personsInCharge[0].id],
            ...TaskPageFilterDrawerTaskTypeParameters.getClearedParametersState(props.taskTypes)
        };
    };

    handleClear = () => {
        const props = this.props;
        this.setState(
            TaskPageFilterDrawer.getClearedParametersState(props)
        );
    };

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        props.onOk(true, state);
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <PageFilterConditionDrawer
                open={props.open}
                onClose={() => this.handleCancel(false)}
            >
                <DrawerContent
                    content={
                        <>
                            {!!props.onSaveFilter && !!props.onRemoveFilter &&
                                <FilterSearchField
                                    filter={props.filter}
                                    canRemoveSharedFilters={props.canRemoveSharedFilters}
                                    onSaveFilter={props.onSaveFilter ? this.handleSaveFilter : null}
                                    onRemoveFilter={props.onRemoveFilter}
                                />
                            }
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.TaskType}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedTaskTypeIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.taskTypes}
                                            onChange={this.handleChangeTaskTypeIds}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.State}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedStateIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            toggleSingleSelectIds={[TaskStateIdFilterActive]}
                                            values={props.states}
                                            onChange={this.handleChangeStateIds}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Priority}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedPriorityIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.priorities}
                                            onChange={this.handleChangePriorityIds} />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Unit}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedUnitIds}
                                            values={props.units}
                                            onChange={this.handleChangeUnitIds} />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.PersonInCharge}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedPersonInChargeUserCodes}
                                            toggleAll={true}
                                            values={props.personsInCharge}
                                            onChange={this.handleChangePersonInChargeUserCodes} />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.TaskHandler}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedHandlerUserCodes}
                                            toggleAll={true}
                                            values={props.handlers}
                                            onChange={this.handleChangeHandlerUserCodes} />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <TaskPageFilterDrawerTaskTypeParameters
                                taskTypes={props.taskTypes}
                                taskType={state.taskType}
                                headerParameters={state.headerParameters}
                                formParameters={state.formParameters}
                                onSetHeaderParameters={this.handleSetHeaderParameters}
                                onSetFormParameters={this.handleSetFormParameters}
                            />
                        </>
                    }
                    actions={
                        <FilterButtons
                            onSave={this.handleSave}
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                        />
                    }
                />
            </PageFilterConditionDrawer>
        );
    }
}
